import React, { useState, useEffect, type ReactNode } from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useInteractions,
  FloatingPortal,
  FloatingFocusManager,
} from "@floating-ui/react";

export interface DropdownItem {
  label: string;
  icon?: ReactNode;
  onClick?: () => void;
  href?: string;
  turboFrame?: string;
  turboMethod?: "get" | "post" | "put" | "delete";
  turboStream?: boolean;
  variant?: "default" | "danger";
  disabled?: boolean;
}

type Placement =
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "top"
  | "top-start"
  | "top-end";

export interface DropdownProps {
  trigger: ReactNode;
  items: DropdownItem[];
  placement?: Placement;
  inline?: boolean;
}

export function Dropdown({
  trigger,
  items,
  placement = "bottom-end",
  inline = false,
}: DropdownProps) {
  const [open, setOpen] = useState(false);

  // Close dropdown when drawer opens
  useEffect(() => {
    const handleDrawerOpened = () => {
      setOpen(false);
    };

    window.addEventListener("drawer:opened", handleDrawerOpened);
    return () => {
      window.removeEventListener("drawer:opened", handleDrawerOpened);
    };
  }, []);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    placement,
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const handleItemClick = (item: DropdownItem) => {
    if (item.disabled) return;
    if (item.onClick) {
      item.onClick();
    }
    setOpen(false);
  };

  const Wrapper = inline ? "span" : "div";

  return (
    <>
      <Wrapper ref={refs.setReference} {...getReferenceProps()}>
        {trigger}
      </Wrapper>
      {open && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
              role="menu"
              className="z-[9999] bg-white rounded-xl shadow-lg border border-slate-200 py-1.5 min-w-[140px] max-h-[350px] overflow-y-auto"
            >
              {items.map((item, index) => {
                const className = `w-full px-3 py-2 text-left text-sm flex items-center gap-2.5 transition-colors ${
                  item.disabled
                    ? "text-slate-300 cursor-not-allowed"
                    : item.variant === "danger"
                      ? "text-red-600 hover:bg-red-50"
                      : "text-slate-700 hover:bg-slate-50"
                }`;

                const content = (
                  <>
                    {item.icon && (
                      <span className={item.disabled ? "opacity-50" : ""}>
                        {item.icon}
                      </span>
                    )}
                    {item.label}
                  </>
                );

                if (item.href) {
                  return (
                    <a
                      key={index}
                      role="menuitem"
                      href={item.href}
                      data-turbo-frame={item.turboFrame}
                      data-turbo-method={item.turboMethod}
                      data-turbo-stream={item.turboStream}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.disabled) {
                          e.preventDefault();
                        } else {
                          setOpen(false);
                        }
                      }}
                      className={className}
                    >
                      {content}
                    </a>
                  );
                }

                return (
                  <button
                    key={index}
                    role="menuitem"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleItemClick(item);
                    }}
                    disabled={item.disabled}
                    className={className}
                  >
                    {content}
                  </button>
                );
              })}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}

// Controlled dropdown - for cases where open state is managed externally
export interface ControlledDropdownProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: ReactNode;
  children: ReactNode;
  placement?: Placement;
}

export function ControlledDropdown({
  open,
  onOpenChange,
  trigger,
  children,
  placement = "bottom-end",
}: ControlledDropdownProps) {
  // Close dropdown when drawer opens
  useEffect(() => {
    const handleDrawerOpened = () => {
      onOpenChange(false);
    };

    window.addEventListener("drawer:opened", handleDrawerOpened);
    return () => {
      window.removeEventListener("drawer:opened", handleDrawerOpened);
    };
  }, [onOpenChange]);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    placement,
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps()}>
        {trigger}
      </div>
      {open && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
              className="z-[9999] bg-white rounded-xl shadow-lg border border-slate-200 py-1.5 min-w-[140px]"
            >
              {children}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}
